import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SearchIcon, ShoppingBagIcon, HeartIcon, UserIcon, MenuIcon, XIcon, ChevronDownIcon, LogOutIcon, PackageIcon } from 'lucide-react';
import { useStore } from '../context/StoreContext';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const {
    cartItemCount,
    wishlist,
    user,
    logout,
    searchQuery,
    setSearchQuery
  } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
    setIsProfileDropdownOpen(false);
  }, [location.pathname]);
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate('/products');
      setIsSearchOpen(false);
    }
  };
  const handleLogout = () => {
    logout();
    navigate('/');
    setIsProfileDropdownOpen(false);
  };
  const categories = ['Shirts', 'T-Shirts', 'Jeans', 'Jackets', 'Hoodies'];
  return <>
      <header className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-3' : 'bg-white py-4 border-b border-slate-100'}`} data-id="element-113">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-114">
          <div className="flex items-center justify-between" data-id="element-115">
            {/* Mobile Menu Button */}
            <button onClick={() => setIsMobileMenuOpen(true)} className="md:hidden p-2 -ml-2 text-slate-600 hover:text-sky-600" data-id="element-116">
              <MenuIcon className="w-6 h-6" data-id="element-117" />
            </button>

            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center gap-2" data-id="element-118">
              <img src="https://cdn.magicpatterns.com/uploads/njJUJ1uVWXUUEU8dnak6Jg/Screenshot_2025-12-23-16-07-24-10_99c04817c0de5652397fc8b56c3b3817.png" alt="Sky Brand" className="h-10 w-auto rounded-full" data-id="element-119" />
              <span className="font-bold text-xl tracking-tight text-slate-900 hidden sm:block" data-id="element-120">
                SKY BRAND
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" data-id="element-121">
              <Link to="/" className="text-sm font-medium text-slate-600 hover:text-sky-600 transition-colors" data-id="element-122">
                Home
              </Link>
              <Link to="/products" className="text-sm font-medium text-slate-600 hover:text-sky-600 transition-colors" data-id="element-123">
                Shop All
              </Link>
              <div className="relative group" data-id="element-124">
                <button className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-sky-600 transition-colors py-2" data-id="element-125">
                  Categories <ChevronDownIcon className="w-4 h-4" data-id="element-126" />
                </button>
                <div className="absolute top-full left-0 w-48 bg-white shadow-lg rounded-lg py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-slate-100" data-id="element-127">
                  {categories.map(cat => <Link key={cat} to={`/products?category=${cat}`} className="block px-4 py-2 text-sm text-slate-600 hover:bg-sky-50 hover:text-sky-600" data-id="element-128">
                      {cat}
                    </Link>)}
                </div>
              </div>
            </nav>

            {/* Right Icons */}
            <div className="flex items-center gap-2 sm:gap-4" data-id="element-129">
              {/* Search Toggle */}
              <button onClick={() => setIsSearchOpen(!isSearchOpen)} className="p-2 text-slate-600 hover:text-sky-600 transition-colors rounded-full hover:bg-slate-50" data-id="element-130">
                <SearchIcon className="w-5 h-5" data-id="element-131" />
              </button>

              {/* Wishlist */}
              <Link to="/wishlist" className="p-2 text-slate-600 hover:text-sky-600 transition-colors rounded-full hover:bg-slate-50 relative hidden sm:block" data-id="element-132">
                <HeartIcon className="w-5 h-5" data-id="element-133" />
                {wishlist.length > 0 && <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white" data-id="element-134">
                    {wishlist.length}
                  </span>}
              </Link>

              {/* Cart */}
              <Link to="/cart" className="p-2 text-slate-600 hover:text-sky-600 transition-colors rounded-full hover:bg-slate-50 relative" data-id="element-135">
                <ShoppingBagIcon className="w-5 h-5" data-id="element-136" />
                {cartItemCount > 0 && <span className="absolute top-0 right-0 w-4 h-4 bg-sky-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white" data-id="element-137">
                    {cartItemCount}
                  </span>}
              </Link>

              {/* User Profile */}
              <div className="relative hidden sm:block" data-id="element-138">
                <button onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)} className="p-2 text-slate-600 hover:text-sky-600 transition-colors rounded-full hover:bg-slate-50 flex items-center gap-2" data-id="element-139">
                  <UserIcon className="w-5 h-5" data-id="element-140" />
                </button>

                {isProfileDropdownOpen && <div className="absolute top-full right-0 mt-2 w-48 bg-white shadow-lg rounded-lg py-2 border border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-141">
                    {user ? <>
                        <div className="px-4 py-2 border-b border-slate-100 mb-2" data-id="element-142">
                          <p className="text-sm font-medium text-slate-900 truncate" data-id="element-143">
                            {user.name}
                          </p>
                          <p className="text-xs text-slate-500 truncate" data-id="element-144">
                            {user.email}
                          </p>
                        </div>
                        <Link to="/my-orders" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-sky-600" data-id="element-145">
                          <PackageIcon className="w-4 h-4" data-id="element-146" /> My Orders
                        </Link>
                        <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 text-left" data-id="element-147">
                          <LogOutIcon className="w-4 h-4" data-id="element-148" /> Logout
                        </button>
                      </> : <>
                        <Link to="/login" className="block px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-sky-600" data-id="element-149">
                          Login / Sign Up
                        </Link>
                      </>}
                  </div>}
              </div>
            </div>
          </div>

          {/* Expandable Search Bar */}
          {isSearchOpen && <div className="py-4 animate-in slide-in-from-top-4 fade-in duration-200" data-id="element-150">
              <form onSubmit={handleSearchSubmit} className="relative max-w-2xl mx-auto" data-id="element-151">
                <input type="text" placeholder="Search for shirts, jeans, hoodies..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-slate-100 border-transparent rounded-full focus:bg-white focus:border-sky-500 focus:ring-2 focus:ring-sky-200 transition-all outline-none" autoFocus data-id="element-152" />
                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-153" />
                <button type="button" onClick={() => setIsSearchOpen(false)} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600" data-id="element-154">
                  <XIcon className="w-4 h-4" data-id="element-155" />
                </button>
              </form>
            </div>}
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 md:hidden" data-id="element-156">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} data-id="element-157" />
          <div className="absolute top-0 left-0 bottom-0 w-4/5 max-w-sm bg-white shadow-xl flex flex-col animate-in slide-in-from-left duration-300" data-id="element-158">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-sky-50" data-id="element-159">
              <div className="flex items-center gap-2" data-id="element-160">
                <img src="https://cdn.magicpatterns.com/uploads/njJUJ1uVWXUUEU8dnak6Jg/Screenshot_2025-12-23-16-07-24-10_99c04817c0de5652397fc8b56c3b3817.png" alt="Sky Brand" className="h-8 w-auto rounded-full" data-id="element-161" />
                <span className="font-bold text-lg text-slate-900" data-id="element-162">
                  SKY BRAND
                </span>
              </div>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-slate-500 hover:bg-slate-200 rounded-full" data-id="element-163">
                <XIcon className="w-5 h-5" data-id="element-164" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4" data-id="element-165">
              {user ? <div className="px-6 py-4 mb-4 bg-slate-50 mx-4 rounded-xl" data-id="element-166">
                  <p className="font-medium text-slate-900" data-id="element-167">Hi, {user.name}</p>
                  <p className="text-sm text-slate-500" data-id="element-168">{user.email}</p>
                </div> : <div className="px-6 mb-6" data-id="element-169">
                  <Link to="/login" className="block w-full py-3 px-4 bg-sky-600 text-white text-center font-medium rounded-lg shadow-sm hover:bg-sky-700" data-id="element-170">
                    Login / Sign Up
                  </Link>
                </div>}

              <nav className="px-4 space-y-1" data-id="element-171">
                <Link to="/" className="block px-4 py-3 text-slate-700 font-medium hover:bg-slate-50 rounded-lg" data-id="element-172">
                  Home
                </Link>
                <Link to="/products" className="block px-4 py-3 text-slate-700 font-medium hover:bg-slate-50 rounded-lg" data-id="element-173">
                  Shop All
                </Link>

                <div className="py-2" data-id="element-174">
                  <p className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2" data-id="element-175">
                    Categories
                  </p>
                  {categories.map(cat => <Link key={cat} to={`/products?category=${cat}`} className="block px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg ml-2" data-id="element-176">
                      {cat}
                    </Link>)}
                </div>

                <div className="py-2 border-t border-slate-100 mt-2" data-id="element-177">
                  <p className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2" data-id="element-178">
                    My Account
                  </p>
                  <Link to="/my-orders" className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg" data-id="element-179">
                    <PackageIcon className="w-5 h-5 text-slate-400" data-id="element-180" /> My Orders
                  </Link>
                  <Link to="/wishlist" className="flex items-center justify-between px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg" data-id="element-181">
                    <div className="flex items-center gap-3" data-id="element-182">
                      <HeartIcon className="w-5 h-5 text-slate-400" data-id="element-183" /> Wishlist
                    </div>
                    {wishlist.length > 0 && <span className="bg-sky-100 text-sky-600 text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-184">
                        {wishlist.length}
                      </span>}
                  </Link>
                </div>
              </nav>
            </div>

            {user && <div className="p-4 border-t border-slate-100" data-id="element-185">
                <button onClick={handleLogout} className="flex items-center gap-2 w-full px-4 py-3 text-red-600 font-medium hover:bg-red-50 rounded-lg transition-colors" data-id="element-186">
                  <LogOutIcon className="w-5 h-5" data-id="element-187" /> Logout
                </button>
              </div>}
          </div>
        </div>}

      {/* Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-40 pb-safe" data-id="element-188">
        <div className="flex items-center justify-around h-16" data-id="element-189">
          <Link to="/" className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${location.pathname === '/' ? 'text-sky-600' : 'text-slate-500'}`} data-id="element-190">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-191">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" data-id="element-192" />
            </svg>
            <span className="text-[10px] font-medium" data-id="element-193">Home</span>
          </Link>
          <Link to="/products" className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${location.pathname === '/products' ? 'text-sky-600' : 'text-slate-500'}`} data-id="element-194">
            <SearchIcon className="w-6 h-6" data-id="element-195" />
            <span className="text-[10px] font-medium" data-id="element-196">Shop</span>
          </Link>
          <Link to="/wishlist" className={`flex flex-col items-center justify-center w-full h-full space-y-1 relative ${location.pathname === '/wishlist' ? 'text-sky-600' : 'text-slate-500'}`} data-id="element-197">
            <div className="relative" data-id="element-198">
              <HeartIcon className="w-6 h-6" data-id="element-199" />
              {wishlist.length > 0 && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 text-white text-[8px] font-bold flex items-center justify-center rounded-full" data-id="element-200">
                  {wishlist.length}
                </span>}
            </div>
            <span className="text-[10px] font-medium" data-id="element-201">Wishlist</span>
          </Link>
          <Link to="/cart" className={`flex flex-col items-center justify-center w-full h-full space-y-1 relative ${location.pathname === '/cart' ? 'text-sky-600' : 'text-slate-500'}`} data-id="element-202">
            <div className="relative" data-id="element-203">
              <ShoppingBagIcon className="w-6 h-6" data-id="element-204" />
              {cartItemCount > 0 && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-sky-500 text-white text-[8px] font-bold flex items-center justify-center rounded-full" data-id="element-205">
                  {cartItemCount}
                </span>}
            </div>
            <span className="text-[10px] font-medium" data-id="element-206">Cart</span>
          </Link>
          <Link to={user ? '/my-orders' : '/login'} className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${['/my-orders', '/login'].includes(location.pathname) ? 'text-sky-600' : 'text-slate-500'}`} data-id="element-207">
            <UserIcon className="w-6 h-6" data-id="element-208" />
            <span className="text-[10px] font-medium" data-id="element-209">Account</span>
          </Link>
        </div>
      </div>

      {/* Spacer to prevent content from hiding behind fixed header */}
      <div className="h-[72px] md:h-[80px]" data-id="element-210"></div>
    </>;
}